import React, { useState } from 'react'
import './SubscriptionManagerPanel.scss'
import Axios from '../../utilities/axios'

const SubscriptionManagerPanel = ({
  subscriptionDetails = {},
  planName = 'pro',
  onClose = () => {}
}) => {
  const [showCancelConfirm, setShowCancelConfirm] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [paymentInfoState, setPaymentInfoState] = useState('idle')

  const { status, currentPeriodEnd, cancelAtPeriodEnd } = subscriptionDetails

  const isActive = status === 'active' || status === 'trial'
  const isPastDue = status === 'past_due'
  const isInactive = status === 'canceled' || status === 'unpaid' || status === 'paused'

  const formatDate = (dateValue) => {
    if (!dateValue) return ''
    const date = new Date(dateValue)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const handleCancelSubscription = async () => {
    setIsLoading(true)
    try {
      await Axios.get('/plan/cancel')
      setShowCancelConfirm(false)
      onClose()
    } catch (error) {
      console.error('Failed to cancel subscription:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handlePaymentInfoRedirection = async () => {
    setPaymentInfoState('loading')
    try {
      const { data } = await Axios.get('/plan/payment-details')
      if (data?.url) {
        window.location.href = data.url
      }
      setPaymentInfoState('opening')
    } catch (error) {
      console.error('Failed to resolve payment:', error)
      setPaymentInfoState('idle')
    }
  }

  const renderStatusBadge = () => {
    if (isActive) {
      return (
        <div className="sm-panel-badge active">
          <i className="fas fa-check-circle"></i>
          Active
        </div>
      )
    }
    if (isPastDue) {
      return (
        <div className="sm-panel-badge warning">
          <i className="fas fa-exclamation-circle"></i>
          Past Due
        </div>
      )
    }
    if (isInactive) {
      return (
        <div className="sm-panel-badge inactive">
          <i className="fas fa-times-circle"></i>
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </div>
      )
    }
    return null
  }

  const renderSubscriptionInfo = () => {
    if (isActive && !cancelAtPeriodEnd) {
      return (
        <div className="sm-panel-message-box">
          <div className="sm-panel-message-title">
            <i className="fas fa-info-circle" style={{ opacity: 0.5 }}></i>
            Subscription Active
          </div>
          <p className="sm-panel-message-text">
            Your subscription is active and will automatically renew on{' '}
            <strong>{formatDate(currentPeriodEnd)}</strong>.
          </p>
        </div>
      )
    }

    if (isActive && cancelAtPeriodEnd) {
      return (
        <div className="sm-panel-message-box">
          <div className="sm-panel-message-title">
            <i className="fas fa-info-circle" style={{ opacity: 0.5 }}></i>
            Subscription Ending
          </div>
          <p className="sm-panel-message-text">
            Your subscription is active but will end on{' '}
            <strong>{formatDate(currentPeriodEnd)}</strong>. You will not be charged again.
          </p>
        </div>
      )
    }

    if (isPastDue) {
      return (
        <div className="sm-panel-message-box warning">
          <div className="sm-panel-message-title">
            <i className="fas fa-info-circle" style={{ opacity: 0.5 }}></i>
            Payment Required
          </div>
          <p className="sm-panel-message-text">
            Your last payment was unsuccessful. Please update your payment method to continue your subscription.
            Your access may be limited until this is resolved.
          </p>
        </div>
      )
    }

    if (isInactive) {
      return (
        <div className="sm-panel-message-box">
          <div className="sm-panel-message-title">
            <i className="fas fa-info-circle" style={{ opacity: 0.5 }}></i>
            Subscription Inactive
          </div>
          <p className="sm-panel-message-text">
            Your subscription is no longer active. To regain access to premium features, please subscribe again.
          </p>
        </div>
      )
    }

    return null
  }

  const renderCancelConfirmation = () => {
    if (!showCancelConfirm) return null

    return (
      <div className="sm-panel-confirm-overlay">
        <div className="sm-panel-confirm-box">
          <div className="sm-panel-message-title" style={{marginBottom: '15px'}}>
            <i className="fal fa-exclamation-triangle" style={{ color: 'var(--sm-warning-color)' }}></i>
            Cancel Subscription?
          </div>
          <p className="sm-panel-message-text">
            Are you sure you want to cancel your subscription? You will retain access until{' '}
            <strong>{formatDate(currentPeriodEnd)}</strong>, after which your subscription will end.
          </p>
          <div className="sm-panel-confirm-actions">
            <div
              className="sm-panel-action-button secondary"
              onClick={() => setShowCancelConfirm(false)}
            >
              Keep Subscription
            </div>
            <div
              className="sm-panel-action-button danger"
              onClick={handleCancelSubscription}
            >
              {isLoading ? 'Canceling...' : 'Yes, Cancel'}
            </div>
          </div>
        </div>
      </div>
    )
  }

  const renderPaymentInfoButton = () => {
    if (paymentInfoState === 'opening') {
      return 'Redirecting...'
    }
    
    if (paymentInfoState === 'loading') {
      return 'Loading...'
    }

    return (
      <>
        Update Payment Information{isPastDue && <i className="fal fa-exclamation-circle" style={{color: 'orange'}}></i>}
      </>
    )
  }

  return (
    <div className="sm-panel-container">
      <div className="sm-panel-close-button" onClick={onClose}>
        <i className="fal fa-times"></i>
      </div>

      <div className="sm-panel-title">Subscription Management</div>

      <div className="sm-panel-sub-title">
        <i style={{ fontSize: '22px', opacity: 0.5, marginRight: '12px' }} className="fal fa-calendar-alt"></i>
        Subscription Status
      </div>

      <p className="sm-panel-description">
        Manage your subscription details and billing preferences.
      </p>

      <div className="sm-panel-widget">
        <div className="sm-panel-widget-icon">
          <i className="fal fa-box-open"></i>
        </div>
        <div className="sm-panel-widget-body">
          {/* <div className="sm-panel-sub-title" style={{ marginBottom: '12.5px' }}>
            <span style={{ opacity: 0.7 }}>Current Plan</span>
            {renderStatusBadge()}
          </div> */}
          <div className="sm-panel-plan-name">
            {planName === 'custom' ? 'Nelima Custom Plan' : 'Nelima Pro'}{renderStatusBadge()}
          </div>
          {/* {
            isActive && planName !== 'custom' && (
              <a href="/upgrade" className="sm-panel-upgrade-link">
                Get Custom Plan
              </a>
            )
          } */}
          {currentPeriodEnd && (
            <div className="sm-panel-status-row">
              <span className="sm-panel-status-text">
                {cancelAtPeriodEnd ? 'Ends' : 'Renews'}: {formatDate(currentPeriodEnd)}
              </span>
            </div>
          )}
          <div style={{cursor: 'pointer'}}>
            {
              planName !== 'custom' &&
              <a href="/upgrade" className="sm-panel-upgrade-link">
                <i className="far fa-arrow-circle-up"></i>Get Custom Plan
              </a>
            }
            <a className="sm-panel-upgrade-link" 
              onClick={(event) => {
                event.preventDefault()
                handlePaymentInfoRedirection()
              }}
              href='/account'
            >
              <i className="fal fa-credit-card"></i>
              {renderPaymentInfoButton()}
            </a>
          </div>
        </div>
      </div>

      {renderSubscriptionInfo()}

      {(isActive || isPastDue) && !cancelAtPeriodEnd && (
        <div
          className="sm-panel-action-button"
          style={{color: 'var(--sm-error-color)'}}
          onClick={() => setShowCancelConfirm(true)}
        >
          Cancel Subscription
        </div>
      )}

      {renderCancelConfirmation()}
    </div>
  )
}

export default SubscriptionManagerPanel
